/** What the Save button on the minutes editor has to go on. */
export interface MinutesSaveInputs {
  /** A save is already in flight. */
  saving: boolean
  /**
   * The project roster cannot yet validate assignees for the project about to
   * be written, so saving now could store ids belonging to another project.
   */
  rosterBlocking: boolean
  /** The committed draft differs from the meeting the server last returned. */
  dirty: boolean
  /**
   * A field that buffers its own keystrokes has focus.
   *
   * Those fields only hand their value up on blur, so while one is focused the
   * committed draft is a render behind what the reader can see, and `dirty`
   * cannot be trusted to say no.
   */
  mayHoldUncommittedEdit: boolean
}

/**
 * Whether Save should refuse the cursor (BRAI-704).
 *
 * The two bugs this sits between pull in opposite directions, and the thing
 * that reconciles them is *when* the draft can be believed.
 *
 * Gating purely on `dirty` is what BRAI-691 was: the editor's fields buffer
 * keystrokes and only commit on blur, so the flag lags the field being typed
 * in. The button was drawn dim and refusing the cursor over edits that were
 * real, and the click that blurred the field enabled it mid-gesture — dimmed,
 * yet saving when pressed.
 *
 * Dropping the gate altogether, which is what that fix did, is BRAI-704: open
 * the editor, touch nothing, and Save invites a press that can do nothing,
 * because there is nothing to save.
 *
 * `dirty` is only ever wrong in one direction and only for one reason: a
 * focused field holding keystrokes it has not handed up yet. So it is believed
 * whenever no such field has focus, and the button stays pressable whenever
 * one does. Nothing typed is then a disabled button, and anything typed keeps
 * Save reachable from the very keystroke, without waiting for a blur.
 */
export function isMinutesSaveDisabled({
  saving,
  rosterBlocking,
  dirty,
  mayHoldUncommittedEdit,
}: MinutesSaveInputs): boolean {
  if (saving || rosterBlocking) return true
  return !dirty && !mayHoldUncommittedEdit
}
